import { useEffect, useState } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { collectionPath } from "@/common/urls";
import { Link } from "react-router";
import { ChevronDown, GripVertical } from "lucide-react";

import { usePinnedCollections } from "@/api/hooks";
import { markPinnedNavigation } from "@/state/pinnedNavigation";
import { useReorderCollectionPinsMutation } from "@/api/api";
import { HGCollection } from "@/common/types";
import { cn } from "@/lib/utils";
import CollectionPinButton from "./CollectionPinButton";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../sidebar";

function PinnedItem({
  collection,
  isActive,
  onActiveClick,
  onNavigate,
}: {
  collection: HGCollection;
  isActive: boolean;
  /** Clicking the already-open collection runs this instead of navigating:
   * it shows all of the collection's records, or flips the time/notes tab. */
  onActiveClick?: (() => void) | null;
  /** The link was followed — as opposed to swallowed by `onActiveClick`. */
  onNavigate?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.id });

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group/pin border-b border-sidebar-border/30 last:border-b-0",
        isDragging && "z-10 opacity-80",
      )}
    >
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className="h-11 gap-2 rounded-none px-3 pr-11 text-[13px] text-ink2 data-[active=true]:font-semibold data-[active=true]:[box-shadow:inset_2px_0_0_var(--primary)] md:h-[34px] md:pr-8"
      >
        <Link
          to={collectionPath(collection)}
          onClick={(e) => {
            if (isActive && onActiveClick) {
              e.preventDefault();
              onActiveClick();
              return;
            }
            markPinnedNavigation();
            onNavigate?.();
          }}
        >
          {/* Reserved, never drawn: it lines the titles here up with the ones
              in the list below, and it is the slot the grip sits in. */}
          <span aria-hidden className="size-[5px] shrink-0" />
          <span className="min-w-0 flex-1 truncate">{collection.title}</span>
        </Link>
      </SidebarMenuButton>
      {/* Pointer-only: it is revealed by hover, so on a touch screen it is an
          invisible target sitting on the left edge of every row. */}
      <button
        type="button"
        aria-label="Reorder pinned collection"
        {...attributes}
        {...listeners}
        className="absolute left-2 top-1/2 hidden -translate-y-1/2 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/pin:opacity-100 md:block [&>svg]:size-3.5"
      >
        <GripVertical />
      </button>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 md:right-1.5">
        <CollectionPinButton
          collection={collection}
          className="size-11 md:size-5"
        />
      </div>
    </SidebarMenuItem>
  );
}

const COLLAPSED_STORAGE_KEY = "pinnedCollectionsCollapsed";

export default function PinnedCollections({
  activeCollectionID,
  onActiveClick,
  onNavigate,
}: {
  activeCollectionID?: string;
  onActiveClick?: (() => void) | null;
  /** A link in this list was followed — the sheet it lives in should close. */
  onNavigate?: () => void;
}) {
  const { pinned } = usePinnedCollections();
  const [reorderPins] = useReorderCollectionPinsMutation();
  const [orderedIDs, setOrderedIDs] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true",
  );

  // Re-seed local order only when the SET of pinned collections changes
  // (pin/unpin) — not when we ourselves reorder — so a persisted reorder
  // doesn't fight the optimistic local order and cause a visible jump.
  const membershipKey = pinned
    .map((c) => c.id)
    .sort()
    .join(",");
  useEffect(() => {
    setOrderedIDs(pinned.map((c) => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipKey]);

  if (pinned.length === 0) return null;

  const byID = new Map(pinned.map((c) => [c.id, c]));
  const ordered = orderedIDs
    .map((id) => byID.get(id))
    .filter((c): c is HGCollection => Boolean(c));

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIDs.indexOf(active.id as string);
    const newIndex = orderedIDs.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIDs, oldIndex, newIndex);
    setOrderedIDs(next);
    reorderPins(next);
  };

  return (
    <SidebarGroup className="gap-1 p-0 pt-2 pb-0">
      <SidebarGroupLabel
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        className="h-9 cursor-pointer select-none justify-between px-4 text-[10.5px] font-bold uppercase tracking-[.1em] md:h-7"
      >
        Pinned
        <ChevronDown
          className={cn(
            "size-3 shrink-0 transition-transform",
            collapsed && "-rotate-90",
          )}
        />
      </SidebarGroupLabel>
      {!collapsed && (
        <SidebarGroupContent className="border-t border-sidebar-border">
          <SidebarMenu className="gap-0">
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedIDs}
                strategy={verticalListSortingStrategy}
              >
                {ordered.map((collection) => (
                  <PinnedItem
                    key={collection.id}
                    collection={collection}
                    isActive={activeCollectionID === collection.id}
                    onActiveClick={onActiveClick}
                    onNavigate={onNavigate}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}
